import { useEffect, useState } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  useCollectionBinding,
  SectionContextProvider,
  EditableCollectionBinding,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { clean, stagger } from '../utils/format';
import { useRawProperty } from '../utils/hooks';
import { byDist, poiDesc, travel, typeWords, type Poi } from '../utils/places';
import { PlaceActions } from './SchoolsNearby';

/** MultiHub categories named the way people talk; the slug is the #around-<slug> deep link the home callout uses. */
const CATS: Array<[string, string, string]> = [
  ['Nature & Outdoor Attractions', 'Parks & Outdoors', 'parks'],
  ['Shopping & Retail', 'Shopping & Errands', 'shopping'],
  ['Restaurants', 'Eat & Drink', 'eat'],
  ['Health and Wellness', 'Health & Fitness', 'health'],
  ['Entertainment', 'Things to Do', 'todo'],
  ['Transportation & Transit', 'Getting Around', 'transit'],
  ['Major Employers & Offices', 'Employers', 'employers'],
];
const LIMIT = 8;

export function AroundTheArea() {
  const p = useRawProperty();
  const pois = useCollectionBinding<Poi>('aroundthearea.places', { collection: 'neighborhood_pois' });
  const mapSlot = useBlockSlot('aroundthearea.map_block', { defaultValue: 'google-maps-places' });

  const cats = CATS.map(([type, label, slug]) => ({ type, label, slug, items: pois.filter((x) => x.poi_type === type).sort(byDist) })).filter((c) => c.items.length);
  const [activeType, setActiveType] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [view, setView] = useState<'list' | 'map'>('list');

  useEffect(() => {
    const m = /^#around-([a-z]+)$/.exec(window.location.hash);
    const deep = m && CATS.find(([, , slug]) => slug === m[1]);
    if (deep) {
      setActiveType(deep[0]);
      setTimeout(() => document.getElementById('around')?.scrollIntoView({ block: 'start' }), 80);
    }
  }, []);

  if (!cats.length) return null;
  const active = cats.find((c) => c.type === activeType) || cats[0];
  const list = expanded ? active.items : active.items.slice(0, LIMIT);
  const hasCoords = !!(clean(p.property_address_latitude) && clean(p.property_address_longitude));

  return (
    <section className="section" id="around">
      <div className="section__head" data-reveal>
        <div className="section__lead-block">
          <RichTextContent path="aroundthearea.eyebrow" as="div" className="eyebrow" />
          <RichTextContent path="aroundthearea.title" as="h2" className="h2" />
        </div>
        <RichTextContent path="aroundthearea.lead" as="div" className="section__lead" />
      </div>

      <div
        className="mb-8 flex items-start justify-between gap-6 [&_.pills]:mb-0 [&_.pills]:min-w-0 [&_.pills]:flex-auto max-[767px]:mb-5 max-[767px]:flex-col-reverse max-[767px]:items-stretch max-[767px]:gap-3 max-[767px]:[&_.pills]:[margin:0_calc(-1*var(--gutter))] max-[767px]:[&_.pills]:[padding:0_var(--gutter)]"
        data-reveal
      >
        {view === 'list' ? (
          <div className="pills" role="group" aria-label="Browse places by category">
            {cats.map((c) => (
              <button
                key={c.type}
                className="pillbtn"
                type="button"
                aria-pressed={c.type === active.type}
                onClick={() => {
                  setActiveType(c.type);
                  setExpanded(false);
                }}
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : (
          <div className="pills" />
        )}
        {hasCoords && (
          <div className="viewswitch" role="group" aria-label="View as">
            <button className="viewswitch__btn" type="button" aria-pressed={view === 'list'} onClick={() => setView('list')}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>
              List
            </button>
            <button className="viewswitch__btn" type="button" aria-pressed={view === 'map'} onClick={() => setView('map')}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 14s4.5-4.2 4.5-7.5a4.5 4.5 0 1 0-9 0C3.5 9.8 8 14 8 14Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /><circle cx="8" cy="6.5" r="1.6" stroke="currentColor" strokeWidth="1.6" /></svg>
              Map
            </button>
          </div>
        )}
      </div>

      <EditableCollectionBinding bindingPath="aroundthearea.places" label="Places">
        {view === 'list' && (
          <div
            className="grid grid-cols-[repeat(4,1fr)] gap-6 max-[1199px]:grid-cols-[repeat(3,1fr)] max-[1023px]:grid-cols-[repeat(2,1fr)] max-[767px]:flex max-[767px]:flex-col max-[767px]:gap-3"
            aria-live="polite"
          >
            {list.map((x, k) => {
              const label = typeWords(x.poi_place_primary_type);
              return (
                <article
                  className="flex min-w-0 flex-col overflow-hidden rounded-lg border border-[--q-border] bg-[--q-paper] text-inherit no-underline max-[767px]:flex-row"
                  key={`${x.poi_name}-${k}`}
                  data-reveal
                  style={stagger(k)}
                >
                  <div className="relative aspect-[16/10] shrink-0 overflow-hidden border-b border-b-[--q-border] bg-[--q-surface] [&_img]:absolute [&_img]:inset-0 [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_img]:[transition:transform_var(--q-motion-slow)_var(--q-ease-out)] max-[767px]:aspect-auto max-[767px]:w-[124px] max-[767px]:border-r max-[767px]:border-r-[--q-border] max-[767px]:[border-bottom:0] max-[767px]:[&>.pill]:px-2 max-[767px]:[&>.pill]:py-1">
                    {clean(x.poi_featured_image) ? (
                      <VibesitesImage url={x.poi_featured_image!} alt={x.poi_name || ''} sizes="(min-width: 1200px) 25vw, (min-width: 768px) 33vw, 124px" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-xs font-medium uppercase leading-4 tracking-[--q-tracking-label] text-muted-foreground">
                        {label || 'Nearby'}
                      </div>
                    )}
                    {x.poi_miles_from_property && (
                      <span className="pill pill--paper absolute bottom-3 left-3 max-[767px]:bottom-2 max-[767px]:left-2">{x.poi_miles_from_property}</span>
                    )}
                  </div>
                  <div className="flex min-w-0 flex-auto flex-col gap-1.5 px-5 py-[18px] max-[767px]:gap-1 max-[767px]:px-3.5 max-[767px]:pb-2.5 max-[767px]:pt-3">
                    {label && <div className="label">{label}</div>}
                    <h3 className="m-0 font-heading text-[22px] font-normal leading-7 [overflow-wrap:anywhere] max-[767px]:text-[19px] max-[767px]:leading-6">
                      {x.poi_name}
                    </h3>
                    {poiDesc(x) && (
                      <p className="m-0 line-clamp-2 text-sm leading-5 text-muted-foreground max-[767px]:text-[13px] max-[767px]:leading-[18px]">{poiDesc(x)}</p>
                    )}
                    <div className="text-[13px] leading-[18px] text-muted-foreground max-[767px]:text-[12px] max-[767px]:leading-4">{travel(x)}</div>
                    <PlaceActions p={x} />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </EditableCollectionBinding>
      {view === 'list' && active.items.length > LIMIT && (
        <div className="mt-8 flex justify-center [&_.btn]:w-auto [&_.btn]:min-w-[200px] max-[767px]:mt-5 max-[767px]:[&_.btn]:w-full">
          <button className="btn btn-secondary" type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
            {expanded ? 'Show Fewer' : 'Show More'}
          </button>
        </div>
      )}

      {view === 'map' && (
        <EditableToken path="aroundthearea.map_block" as="div" className="block min-h-[420px] overflow-hidden rounded-lg">
          {mapSlot ? (
            <SectionContextProvider sectionId={mapSlot.sectionId}>
              <mapSlot.BlockComponent sectionId={mapSlot.sectionId} />
            </SectionContextProvider>
          ) : (
            <div className="section__note">No map selected</div>
          )}
        </EditableToken>
      )}
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AroundTheArea',
  displayName: 'Around the Area',
  tokens: {
    'aroundthearea.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Around the area</p>' },
    'aroundthearea.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Everyday errands and weekend plans, minutes away.</p>' },
    'aroundthearea.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue:
        '<p>Browse what is around <span data-type="field-token" data-field="property_name"></span>: groceries and pharmacies, dinners out, parks and trails, and the places you will use every week. Distances and drive times are from the community.</p>',
    },
    'aroundthearea.map_block': {
      label: 'Map',
      type: 'block',
      defaultValue: 'google-maps-places',
      acceptedBlockCategories: ['Embeds'],
    },
  },
  collectionBindings: {
    'aroundthearea.places': {
      label: 'Places',
      collection: 'neighborhood_pois',
    },
  },
};

(AroundTheArea as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AroundTheArea;
